import { onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { reportAuthFlowBlocked } from '@peanuds/shared/observability/runtime-observability';

import { useAuthSessionStore } from '../store/auth-session.store';
import { AUTH_SESSION_TOKEN_EXPIRY_BUFFER_MS } from '../store/session/session-deadline';

export const AUTH_ACTIVITY_REFRESH_MIN_INTERVAL_MS = 60_000;
export const AUTH_TOKEN_REFRESH_INTERVAL_MS = AUTH_ACTIVITY_REFRESH_MIN_INTERVAL_MS;
export const AUTH_TOKEN_REFRESH_BEFORE_EXPIRY_MS = 30_000;
export const AUTH_TOKEN_REFRESH_EXPIRY_BUFFER_MS = AUTH_SESSION_TOKEN_EXPIRY_BUFFER_MS;
export const AUTH_ACTIVITY_REFRESH_WHEN_REMAINING_MS = 2 * 60 * 1000;
export const AUTH_TOKEN_REFRESH_DEBUG_ENABLED = false;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'input', 'change', 'submit'] as const;
type RefreshActivityReason = (typeof ACTIVITY_EVENTS)[number] | 'navigation';
type RefreshTrigger = 'activity' | 'activity-window' | 'expiry-window';

const shouldLogRefreshDebug =
  import.meta.env.MODE !== 'production' && AUTH_TOKEN_REFRESH_DEBUG_ENABLED;

const toIsoString = (timestamp: number | null) => {
  return timestamp === null ? null : new Date(timestamp).toISOString();
};

export const useAuthTokenRefreshInterval = () => {
  const authSessionStore = useAuthSessionStore();
  const router = useRouter();
  let isRefreshInFlight = false;
  let lastRefreshAttemptAt: number | null = null;
  let hasIgnoredInitialNavigation = false;
  let refreshTimeout: number | null = null;
  const activityReasons = new Set<RefreshActivityReason>();

  const hasActiveSessionDeadline = (now = Date.now()) => {
    return (
      authSessionStore.sessionActiveUntil !== null && authSessionStore.sessionActiveUntil > now
    );
  };

  const isTokenNearExpiry = (now = Date.now()) => {
    return (
      authSessionStore.tokenExpiresAt !== null &&
      authSessionStore.tokenExpiresAt > now &&
      authSessionStore.tokenExpiresAt - now <= AUTH_TOKEN_REFRESH_BEFORE_EXPIRY_MS
    );
  };

  const isActivityRefreshWindowOpen = (now = Date.now()) => {
    return (
      authSessionStore.sessionActiveUntil !== null &&
      authSessionStore.sessionActiveUntil > now &&
      authSessionStore.sessionActiveUntil - now <= AUTH_ACTIVITY_REFRESH_WHEN_REMAINING_MS
    );
  };

  const getRefreshThrottleRemainingMs = () => {
    if (lastRefreshAttemptAt === null) {
      return 0;
    }

    return Math.max(AUTH_ACTIVITY_REFRESH_MIN_INTERVAL_MS - (Date.now() - lastRefreshAttemptAt), 0);
  };

  const isRefreshAttemptThrottled = () => {
    return (
      lastRefreshAttemptAt !== null &&
      Date.now() - lastRefreshAttemptAt < AUTH_ACTIVITY_REFRESH_MIN_INTERVAL_MS
    );
  };

  const clearRefreshTimeout = () => {
    if (refreshTimeout === null) {
      return;
    }

    window.clearTimeout(refreshTimeout);
    refreshTimeout = null;
  };

  const scheduleRefreshCheck = (minimumDelayMs = 0) => {
    clearRefreshTimeout();

    if (!authSessionStore.jwtToken || !authSessionStore.idToken || activityReasons.size === 0) {
      return;
    }

    const now = Date.now();
    const refreshChecks: Array<{ trigger: RefreshTrigger; startsAt: number }> = [];

    if (authSessionStore.sessionActiveUntil !== null && authSessionStore.sessionActiveUntil > now) {
      refreshChecks.push({
        trigger: 'activity-window',
        startsAt: authSessionStore.sessionActiveUntil - AUTH_ACTIVITY_REFRESH_WHEN_REMAINING_MS,
      });
    }

    if (authSessionStore.tokenExpiresAt !== null && authSessionStore.tokenExpiresAt > now) {
      refreshChecks.push({
        trigger: 'expiry-window',
        startsAt: authSessionStore.tokenExpiresAt - AUTH_TOKEN_REFRESH_BEFORE_EXPIRY_MS,
      });
    }

    const nextRefreshCheck = refreshChecks.reduce<(typeof refreshChecks)[number] | null>(
      (nextCheck, refreshCheck) => {
        if (nextCheck === null || refreshCheck.startsAt < nextCheck.startsAt) {
          return refreshCheck;
        }

        return nextCheck;
      },
      null,
    );

    if (nextRefreshCheck === null) {
      return;
    }

    const delayMs = Math.max(nextRefreshCheck.startsAt - now, minimumDelayMs, 0);

    refreshTimeout = window.setTimeout(() => {
      void refreshTokenIfNeeded(nextRefreshCheck.trigger);
    }, delayMs);
  };

  const markActivity = (reason: RefreshActivityReason) => {
    const now = Date.now();

    if (authSessionStore.jwtToken && authSessionStore.idToken) {
      authSessionStore.setLastActiveTime(now);
    }

    activityReasons.add(reason);

    if (shouldLogRefreshDebug) {
      console.info('[auth-refresh] activity marked for next refresh', {
        reason,
        activityReasons: [...activityReasons],
        refreshWhenRemainingMs: AUTH_ACTIVITY_REFRESH_WHEN_REMAINING_MS,
        sessionActiveUntil: toIsoString(authSessionStore.sessionActiveUntil),
        msSinceLastRefreshAttempt:
          lastRefreshAttemptAt === null ? null : Date.now() - lastRefreshAttemptAt,
      });
    }

    void refreshTokenIfNeeded('activity');
  };

  const refreshTokenIfNeeded = async (trigger: RefreshTrigger) => {
    if (isRefreshInFlight) {
      return;
    }

    if (!authSessionStore.jwtToken || !authSessionStore.idToken) {
      activityReasons.clear();
      return;
    }

    if (!hasActiveSessionDeadline()) {
      activityReasons.clear();

      if (shouldLogRefreshDebug) {
        console.info('[auth-refresh] silent refresh skipped because session is not active', {
          sessionActiveUntil: toIsoString(authSessionStore.sessionActiveUntil),
        });
      }

      return;
    }

    if (activityReasons.size === 0) {
      if (shouldLogRefreshDebug) {
        console.info('[auth-refresh] token refresh skipped because no user activity was recorded', {
          trigger,
        });
      }

      return;
    }

    if (!isTokenNearExpiry() && !isActivityRefreshWindowOpen()) {
      if (shouldLogRefreshDebug) {
        console.info('[auth-refresh] token refresh skipped because no refresh window is open', {
          trigger,
          tokenExpiresAt: toIsoString(authSessionStore.tokenExpiresAt),
          refreshWindowStartsAt: toIsoString(
            authSessionStore.tokenExpiresAt === null
              ? null
              : authSessionStore.tokenExpiresAt - AUTH_TOKEN_REFRESH_BEFORE_EXPIRY_MS,
          ),
          sessionActiveUntil: toIsoString(authSessionStore.sessionActiveUntil),
          activityRefreshWindowStartsAt: toIsoString(
            authSessionStore.sessionActiveUntil === null
              ? null
              : authSessionStore.sessionActiveUntil - AUTH_ACTIVITY_REFRESH_WHEN_REMAINING_MS,
          ),
        });
      }

      scheduleRefreshCheck();
      return;
    }

    if (isRefreshAttemptThrottled()) {
      const throttleRemainingMs = getRefreshThrottleRemainingMs();

      if (shouldLogRefreshDebug) {
        console.info('[auth-refresh] token refresh skipped because last attempt was too recent', {
          trigger,
          msSinceLastRefreshAttempt:
            lastRefreshAttemptAt === null ? null : Date.now() - lastRefreshAttemptAt,
          retryAfterMs: throttleRemainingMs,
        });
      }

      scheduleRefreshCheck(throttleRemainingMs);
      return;
    }

    const refreshActivityReasons = [...activityReasons];
    activityReasons.clear();
    isRefreshInFlight = true;
    lastRefreshAttemptAt = Date.now();

    const startedAt = Date.now();
    const previousToken = authSessionStore.jwtToken;
    const previousTokenExpiresAt = authSessionStore.tokenExpiresAt;

    try {
      const refreshedToken = await authSessionStore.refreshAuthClaims();
      const nextTokenExpiresAt = authSessionStore.tokenExpiresAt;
      const didExtendTokenExpiry =
        nextTokenExpiresAt !== null &&
        (previousTokenExpiresAt === null || nextTokenExpiresAt > previousTokenExpiresAt);

      if (refreshedToken !== null && nextTokenExpiresAt !== null) {
        authSessionStore.setSessionActiveUntil(nextTokenExpiresAt);
      }

      if (shouldLogRefreshDebug) {
        console.info('[auth-refresh] token refresh check completed', {
          trigger,
          durationMs: Date.now() - startedAt,
          hadRefreshResult: refreshedToken !== null,
          tokenChanged: refreshedToken !== null && refreshedToken !== previousToken,
          previousTokenExpiresAt: toIsoString(previousTokenExpiresAt),
          nextTokenExpiresAt: toIsoString(nextTokenExpiresAt),
          didExtendTokenExpiry,
          sessionActiveUntil: toIsoString(authSessionStore.sessionActiveUntil),
          activityReasons: refreshActivityReasons,
        });
      }
    } catch (error) {
      reportAuthFlowBlocked({
        operation: 'silent-refresh',
        failureKind: 'refresh-failed',
        tags: {
          isLoggedIn: authSessionStore.isLoggedIn,
          hasJwtToken: Boolean(authSessionStore.jwtToken),
          isTokenExpired: authSessionStore.isTokenExpired,
        },
      });

      if (shouldLogRefreshDebug) {
        console.warn('[auth-refresh] token refresh failed', {
          trigger,
          durationMs: Date.now() - startedAt,
          previousTokenExpiresAt: toIsoString(previousTokenExpiresAt),
          currentTokenExpiresAt: toIsoString(authSessionStore.tokenExpiresAt),
          activityReasons: refreshActivityReasons,
          error,
        });
      }
    } finally {
      isRefreshInFlight = false;
      scheduleRefreshCheck();
    }
  };

  const stopTokenExpiryWatcher = watch(
    () => [authSessionStore.tokenExpiresAt, authSessionStore.sessionActiveUntil],
    () => {
      scheduleRefreshCheck();
    },
    { immediate: true },
  );
  const removeRouterActivityListener = router.afterEach(() => {
    if (!hasIgnoredInitialNavigation) {
      hasIgnoredInitialNavigation = true;

      if (shouldLogRefreshDebug) {
        console.info('[auth-refresh] initial navigation ignored for refresh activity');
      }

      return;
    }

    markActivity('navigation');
  });
  const activityEventHandlers = ACTIVITY_EVENTS.map((eventName) => ({
    eventName,
    handler: () => {
      markActivity(eventName);
    },
  }));

  activityEventHandlers.forEach(({ eventName, handler }) => {
    window.addEventListener(eventName, handler);
  });

  if (shouldLogRefreshDebug) {
    console.info('[auth-refresh] activity-gated token maintenance enabled', {
      minAttemptIntervalMs: AUTH_TOKEN_REFRESH_INTERVAL_MS,
      activityRefreshMinIntervalMs: AUTH_ACTIVITY_REFRESH_MIN_INTERVAL_MS,
      activityRefreshWhenRemainingMs: AUTH_ACTIVITY_REFRESH_WHEN_REMAINING_MS,
      refreshBeforeExpiryMs: AUTH_TOKEN_REFRESH_BEFORE_EXPIRY_MS,
      activityReasons: [...ACTIVITY_EVENTS, 'navigation'],
    });
  }

  onUnmounted(() => {
    clearRefreshTimeout();
    stopTokenExpiryWatcher();
    removeRouterActivityListener();

    activityEventHandlers.forEach(({ eventName, handler }) => {
      window.removeEventListener(eventName, handler);
    });
  });
};
